import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FileExplorer from '../components/storage/FileExplorer';
import {
  CloudIcon,
  CircleStackIcon,
  ArrowTrendingUpIcon,
  CubeIcon,
  SparklesIcon,
  CpuChipIcon,
  DocumentMagnifyingGlassIcon,
  BeakerIcon,
  ServerIcon
} from '@heroicons/react/24/outline';
import { formatBytes, formatCurrency } from '../utils/formatters';
import { getApiUrl } from '../utils/api';

interface StorageStats {
  totalFiles: number;
  totalSize: number;
  lastSync: string | null;
  tier: string;
  includedGb: number;
  overageGb: number;
  overageRate: number;
  monthlyCost: number;
  billingStatus: string;
}


export default function StoragePage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<StorageStats>({
    totalFiles: 0,
    totalSize: 0,
    lastSync: null,
    tier: 'standard',
    includedGb: 10,
    overageGb: 0,
    overageRate: 0.10,
    monthlyCost: 0,
    billingStatus: 'ok',
  });
  const [, setLoading] = useState(true);

  useEffect(() => {
    fetchStorageUsage();
  }, []);

  const fetchStorageUsage = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await fetch(`${getApiUrl()}/api/v1/storage/usage`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStats({
          totalSize: data.used_bytes || 0,
          totalFiles: data.object_count || 0,
          lastSync: data.last_sync || null,
          tier: data.storage_tier || 'standard',
          includedGb: data.included_gb ?? 10,
          overageGb: data.overage_gb ?? 0,
          overageRate: data.overage_rate ?? 0.10,
          monthlyCost: data.monthly_cost ?? 0,
          billingStatus: data.billing_status ?? 'ok',
        });
      }
    } catch (err) {
      console.error('Error fetching storage usage:', err);
    } finally {
      setLoading(false);
    }
  };



  const tierDisplayName = stats.tier === 'standard' ? 'Free' : stats.tier === 'premium' ? 'Pro' : stats.tier.charAt(0).toUpperCase() + stats.tier.slice(1);

  const progressPct = Math.min(100, (stats.totalSize / (stats.includedGb * 1024 * 1024 * 1024)) * 100);
  const progressColor = stats.billingStatus === 'ok'
    ? 'from-green-500 to-green-400'
    : stats.billingStatus === 'warning'
    ? 'from-yellow-500 to-yellow-400'
    : 'from-red-500 to-red-400';

  return (
    <div className="min-h-screen bg-black">
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        {/* Compact Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white">Cloud Storage</h1>
          <p className="mt-1 text-sm text-gray-400">
            Manage your notebooks, datasets, and models with intelligent processing
          </p>
        </div>

        {/* AI Data Intelligence - Prominent Top Section */}
        <div className="mb-6 p-4 bg-gradient-to-r from-blue-900/30 to-purple-900/30 border border-blue-800/50 rounded-xl">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 p-2 bg-blue-900/50 rounded-lg">
              <SparklesIcon className="h-6 w-6 text-blue-400" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-base font-semibold text-white">
                  AI Data Intelligence
                </h3>
                <span className="px-2 py-0.5 text-xs font-medium bg-blue-900/50 text-blue-300 rounded-full border border-blue-700/50">
                  Coming Soon
                </span>
              </div>
              <p className="text-xs text-gray-300 mb-3">
                Our autonomous agents will continuously analyze your uploaded data, creating comprehensive data dictionaries,
                detecting schemas, and building semantic indexes for intelligent querying.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="flex items-start gap-2">
                  <CpuChipIcon className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-gray-200">Agentic Processing</p>
                    <p className="text-xs text-gray-400">Autonomous data profiling & cataloging</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <DocumentMagnifyingGlassIcon className="h-4 w-4 text-purple-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-gray-200">Smart Discovery</p>
                    <p className="text-xs text-gray-400">Auto-detect patterns & relationships</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <BeakerIcon className="h-4 w-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-gray-200">MCP Integration</p>
                    <p className="text-xs text-gray-400">Natural language data queries</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid with Better Sizing */}
        <div className="grid grid-cols-12 gap-3 mb-6">
          {/* Storage Usage - Larger, More Prominent */}
          <div className="col-span-6 lg:col-span-5 bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-lg p-4">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <CircleStackIcon className="h-5 w-5 text-green-400" />
                <span className="text-sm font-medium text-gray-300">Storage Used</span>
              </div>
            </div>
            <div>
              <p className="text-2xl font-bold text-white mb-1">
                {formatBytes(stats.totalSize)}
              </p>
              <p className="text-sm text-gray-400 mb-3">of {stats.includedGb} GB free tier</p>
              <div className="w-full bg-gray-700 rounded-full h-2">
                <div
                  className={`bg-gradient-to-r ${progressColor} h-2 rounded-full transition-all duration-300`}
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {stats.overageGb > 0
                  ? `${stats.overageGb} GB billable at $${stats.overageRate}/GB/mo`
                  : 'Within free tier'}
              </p>
            </div>
          </div>

          {/* Tier - More Descriptive */}
          <div className="col-span-6 lg:col-span-3 bg-gray-900 border border-gray-800 rounded-lg p-4">
            <div className="flex items-start justify-between mb-2">
              <CloudIcon className="h-5 w-5 text-blue-400" />
              <span className="text-xs text-gray-500">Plan</span>
            </div>
            <p className="text-xl font-bold text-white mb-1">{tierDisplayName}</p>
            <p className="text-xs text-gray-400">
              {stats.includedGb} GB storage included
            </p>
          </div>

          {/* Monthly Cost - More Descriptive */}
          <div className="col-span-6 lg:col-span-4 bg-gray-900 border border-gray-800 rounded-lg p-4">
            <div className="flex items-start justify-between mb-2">
              <ArrowTrendingUpIcon className="h-5 w-5 text-purple-400" />
              <span className="text-xs text-gray-500">This Month</span>
            </div>
            <p className="text-xl font-bold text-white mb-1">
              {formatCurrency(stats.monthlyCost)}
            </p>
            <div className="text-xs text-gray-400">
              {stats.overageGb > 0
                ? <p>{stats.overageGb.toFixed(1)} GB × ${stats.overageRate}/GB</p>
                : <p>No overage charges</p>
              }
            </div>
          </div>

        </div>

        {/* File Explorer */}
        <div style={{ height: 'calc(100vh - 420px)', minHeight: '400px' }}>
          <FileExplorer 
            showUpload={true}
            showUsage={false}
            onFileSelect={(file) => {
              console.log('Selected file:', file);
            }}
          />
        </div>

        {/* Quick Actions - Compact */}
        <div className="mt-6 flex gap-3">
          <button
            onClick={() => navigate('/dashboard/notebooks')}
            className="flex-1 flex items-center justify-center px-3 py-2 border border-gray-800 rounded-lg text-xs font-medium text-gray-300 bg-gray-900 hover:bg-gray-800 transition-colors"
          >
            <ServerIcon className="h-4 w-4 mr-1.5 text-gray-400" />
            Launch Notebook
          </button>
          
          <button
            onClick={() => navigate('/dashboard/data')}
            className="flex-1 flex items-center justify-center px-3 py-2 border border-gray-800 rounded-lg text-xs font-medium text-gray-300 bg-gray-900 hover:bg-gray-800 transition-colors"
          >
            <CubeIcon className="h-4 w-4 mr-1.5 text-gray-400" />
            Marketplace
          </button>
          
          <button
            onClick={() => navigate('/dashboard/billing')}
            className="flex-1 flex items-center justify-center px-3 py-2 border border-gray-800 rounded-lg text-xs font-medium text-gray-300 bg-gray-900 hover:bg-gray-800 transition-colors"
          >
            <ArrowTrendingUpIcon className="h-4 w-4 mr-1.5 text-gray-400" />
            Usage Details
          </button>
        </div>
      </div>
    </div>
  );
}